import type { Household } from 'src/types/household';
import { Button } from '@savvy-kit/components/button';
import { toast } from 'sonner';
import { ORION_URL } from 'src/constants/logo-urls/financial-institution-logo';
import { graphql } from 'src/gql';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { providerImgSrc } from 'src/utils/image';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

export const UNLINK_ORION_HOUSEHOLD_MUTATION = graphql(/* GraphQL */ `
  mutation UnlinkOrionHousehold($input: UnlinkOrionHouseholdInput!) {
    unlinkOrionHousehold(input: $input) {
      success
    }
  }
`);

type UnlinkFromOrionButtonProps = {
  readonly household: Pick<Household, 'id' | 'name'>;
  readonly orionHouseholdId: number;
};

function UnlinkFromOrionButton({ household, orionHouseholdId }: UnlinkFromOrionButtonProps) {
  const showConfirmation = useConfirmationDialog();

  const [unlinkOrionHousehold] = useMutationWithErrorLogging(UNLINK_ORION_HOUSEHOLD_MUTATION);

  // Throws on failure so the confirmation dialog stays open for a retry; success reloads the page.
  const handleUnlink = async () => {
    const toastId = toast.loading('Unlinking...');

    let succeeded = false;
    try {
      const { data } = await unlinkOrionHousehold({ variables: { input: { householdId: household.id } } });
      succeeded = data?.unlinkOrionHousehold?.success ?? false;
    } catch (error) {
      handleMutationError(error, 'Failed to unlink Orion household', { id: toastId });
      throw error;
    }

    if (!succeeded) {
      toast.error('Failed to unlink Orion household', { id: toastId });
      throw new Error('Failed to unlink Orion household');
    }

    toast.success('Orion household unlinked. You can reconnect with the correct ID.', { id: toastId });
    window.location.reload();
  };

  const handleClick = () => {
    showConfirmation({
      title: `Unlink ${household.name} from Orion?`,
      description: (
        <>
          This clears the current Orion connection (HH {orionHouseholdId}). The accounts, holdings, and transactions
          Savvy mirrors from Orion are deleted and re-sync when you reconnect, so nothing is lost on the Orion side. But
          reconnecting creates new account records, so Savvy data tied to the current accounts is permanently removed
          and won&apos;t come back: alerts, RMDs, bank-account authorizations, account fundings, and in-progress
          requests (trades, SWIM transfers, and account-confirmation tasks). Only unlink a household that&apos;s
          connected to the wrong Orion ID.
        </>
      ),
      confirmationLabel: 'Unlink',
      confirmationVariant: 'destructive',
      onConfirmation: handleUnlink,
    });
  };

  return (
    <Button
      variant="secondary"
      onClick={handleClick}
      icon={
        // eslint-disable-next-line @next/next/no-img-element -- providerImgSrc returns a tiny provider icon URL for button chrome
        <img src={providerImgSrc(ORION_URL)} alt="" className="size-4 bg-inherit" />
      }
      iconPosition="right"
    >
      Unlink from Orion
    </Button>
  );
}

export default UnlinkFromOrionButton;
